import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ApplianceGroup, DataRef, DataRefsByKey, LocalizedKeyValue, ParameterSettings, Program } from '@shared/models/dataref.model';
import { RefDataService } from '@services/ref-data.service';
import { OperationLite, ProgramParameterLite, SequenceLite } from '@shared/models/recipe/step-lite.model';
import { Subject, Subscription } from 'rxjs';
import { DataSource } from '@angular/cdk/table';
import { IngredientLite } from '@shared/models/ingredient/ingredient-lite.model';
import { RecipeLite } from '@shared/models/recipe/recipe-lite.model';
import { takeUntil } from 'rxjs/operators';
import { ConfigService } from '@services/config.service';
import { Utils } from '@shared/utils/utils';

@Component({
  selector: 'app-programme-inputs',
  templateUrl: './programme-inputs.component.html',
  styleUrls: ['./programme-inputs.component.scss'],
  standalone: false,
})
export class ProgrammeInputsComponent implements OnChanges, OnDestroy {
  @Input() formGroupStep?: UntypedFormGroup;
  @Input() formVariant?: UntypedFormGroup;
  @Input() applianceGroupsList?: ApplianceGroup[] = [];
  @Input() programList?: Program[] = [];
  @Input() parameterSettingsList?: ParameterSettings[] = [];
  @Input() psNamesList?: DataRef[] = [];
  @Input() variant?: RecipeLite | null;
  hourText = 'h';
  minText = 'm';
  secText = 's';
  knownPrograms = ['DURATION', 'COUNTDOWN', 'SPEED', 'TEMPERATURE', 'KEEP_WARM', 'ALERT_TEXT', 'ALERT_TYPE', 'INGREDIENT'];
  applianceGroups: string[] = [];
  defaultApplianceGroup = '';
  dataSource: { data: DataSource<Program>[]; columns: string[] }[] = [];
  newProgram: string[] = [];
  defaultLanguages: {
    market: string;
    lang: string;
    domain: string;
  };
  dropDowns: { key: string; value: ParameterSettings }[][] = [];
  timeMask = '00h00m00s';
  timePatterns = {
    '0': { pattern: /\d/ },
  };
  specialCharacters = ['h', 'm', 's'];

  private columnCounter = 0;
  private applianceGroupsSubscription: Subscription | undefined;
  private defaultApplianceGroupSubscription: Subscription | undefined;
  private readonly destroy$ = new Subject<void>();

  constructor(
    protected refDataService: RefDataService,
    private changeDetectorRefs: ChangeDetectorRef,
    private configService: ConfigService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.defaultLanguages = this.configService.getDefaultSearchQueryParams();
    if (changes.formGroupStep) {
      const formGroupStep = changes.formGroupStep.currentValue;
      if (formGroupStep && formGroupStep.controls && formGroupStep.controls.sequences) {
        const sequences = formGroupStep.controls?.sequences?.value;
        sequences?.forEach((sequence: SequenceLite) => {
          sequence.operations?.forEach((operation: OperationLite) => {
            operation.parameters?.forEach((param: ProgramParameterLite) => {
              const matchingParam = this.parameterSettingsList?.find((paramSet) => paramSet.key === param.key);
              if (matchingParam?.isVisible !== null && matchingParam?.isVisible !== undefined) {
                param.isVisible = matchingParam.isVisible;
              }
            });
          });
        });
      }
      this.createDataSource(changes.formGroupStep.currentValue);
    }
    if (changes.formVariant?.currentValue) {
      const formGroup = changes.formVariant.currentValue;
      this.applianceGroupsSubscription = this.subscribeToFormValueChanges(formGroup, 'applianceGroups', (applianceGroups: string[]) => {
        this.applianceGroups = applianceGroups;
        this.createDataSource(this.formVariant?.controls.steps.value);
      });
      this.defaultApplianceGroupSubscription = this.subscribeToFormValueChanges(
        formGroup,
        'defaultApplianceGroup',
        (defaultApplianceGroup: string) => {
          this.defaultApplianceGroup = defaultApplianceGroup;
          this.createDataSource(this.formVariant?.controls.steps.value);
        },
      );
    }
  }

  createDataSource(formGroupStep: UntypedFormGroup | undefined): void {
    this.dataSource = [];
    this.newProgram = [];
    this.dropDowns = [];

    this.changeDetectorRefs.detectChanges(); // pour réinitialiser l'affichage

    if (this.formVariant && formGroupStep) {
      if (formGroupStep?.controls?.sequences?.value) {
        for (const sequence of formGroupStep?.controls?.sequences?.value) {
          let tab: string[] = [];
          const dropDowns: { key: string; value: ParameterSettings }[] = [];

          if (sequence?.operations) {
            for (const operation of sequence.operations) {
              if (operation.parameters) {
                const updatedParams: ParameterSettings[] =
                  operation.parameters
                    ?.filter(
                      (param: ParameterSettings) =>
                        this.parameterSettingsList?.find((paramSet: ParameterSettings) => paramSet.key === param.key)?.isVisible ?? false,
                    )
                    .map((currentValue: ParameterSettings) => this.updateProgramsParameters(operation, currentValue)) ?? [];

                operation.parameters = updatedParams;

                tab = [...tab, ...updatedParams.map((param) => param.commonKey).filter((param) => this.knownPrograms.includes(param))];
                // Mettre à jour les valeurs temporelles si nécessaire
                operation.parameters?.forEach((param: ParameterSettings) => {
                  if (param.commonKey === 'DURATION' || param.commonKey === 'COUNTDOWN') {
                    param.valueTemp = this.convertTimeToText(Number(param.value));
                  }
                });
                updatedParams
                  ?.filter((param: ParameterSettings) => param.commonKey === 'DROPDOWN')
                  .forEach((param: ParameterSettings) => {
                    const columnName = `${param.commonKey}_${this.columnCounter++}`;
                    if (!dropDowns.some((item) => item.value.key === param.key)) {
                      dropDowns.push({ key: columnName, value: param });
                    }
                  });
              } else {
                this.changeProgram(operation);
              }
            }
          }
          //remove duplicate columns
          tab = [...new Set(tab)];
          this.dataSource.push({
            data: sequence?.operations,
            columns: ['program', ...tab, ...dropDowns.map(({ key }) => key), 'action'],
          });
          this.dropDowns.push(dropDowns);
        }

        this.changeDetectorRefs.detectChanges();
      }
    }
  }

  getParameterIndexByCommonKey(parameter: ParameterSettings[], commonKey: string): number {
    return parameter.findIndex((param) => param.commonKey === commonKey);
  }

  getGenericParameterIndexByCommonKey(parameter: ParameterSettings[], commonKey: { [p: string]: string }): number {
    return parameter.findIndex((param) => param.name?.key === commonKey);
  }

  getParameterSettingByKey(key: string): ParameterSettings | undefined {
    return this.parameterSettingsList?.find((param) => param.key === key);
  }

  getGenericParameterSettingByKey(key: string): ParameterSettings | undefined {
    const keyString = Object.values(key).join('');
    return this.parameterSettingsList?.find((param) => param.key === keyString);
  }

  getIngredientIDList(): IngredientLite[] {
    return this.variant?.ingredients.filter((ing: IngredientLite) => this.formGroupStep?.value.ingredients?.includes(ing.identifier)) ?? [];
  }

  getIngredientName(key: string): string {
    return this.variant?.ingredients.find((ing: IngredientLite) => ing.topIngredientFid === key)?.applicationDescription ?? key;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filterProgram(applianceGroupKey: string): LocalizedKeyValue[] {
    return (this.programList ?? []).filter((program: DataRef) => (program as Program).applianceGroup?.key === applianceGroupKey);
  }

  applianceText(key: string): string | undefined {
    const applianceIndex = this.applianceGroupsList?.findIndex((appliance: ApplianceGroup) => appliance.key === key);
    return applianceIndex !== undefined && applianceIndex >= 0 && this.applianceGroupsList
      ? this.applianceGroupsList[applianceIndex].label
      : key;
  }

  getSanitizedId(value: string): string {
    return Utils.getSanitizedId(value);
  }

  getName(key: string, list: DataRef[] | undefined): string {
    const obj = (list ?? []).find((object: DataRef) => object.key === key);
    if (obj) {
      return obj.translatedName ?? '';
    }
    return '';
  }

  getGenericName(key: { [p: string]: string }, list: DataRef[] | undefined): string {
    const keyString = Object.values(key).join('');
    const obj = (list ?? []).find((object: DataRef) => object.key === keyString);
    if (obj) {
      return obj.translatedName ?? '';
    }
    return '';
  }
  convertSecToHoursMinSec(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    let time = hours > 0 ? hours.toString().padStart(2, '0') : '00';
    time += minutes > 0 ? minutes.toString().padStart(2, '0') : '00';
    time += seconds > 0 ? seconds.toString().padStart(2, '0') : '00';
    return time;
  }

  convertTimeToText(sec: number | undefined): string {
    if (sec) {
      const time = this.convertSecToHoursMinSec(sec);
      return time.substr(0, 2) + this.hourText + time.substr(2, 2) + this.minText + time.substr(4, 2) + this.secText;
    }
    return '';
  }

  getParameterName(commonKey: string): string {
    let paramName = '';
    this.dataSource.forEach((program: { data: DataSource<Program>[]; columns: string[] }) => {
      program?.data?.forEach((data: any) => {
        data.parameters?.forEach((param: { commonKey: string; name: { name: { [x: string]: any }; key: string } }) => {
          if (param.commonKey === commonKey) {
            const marketLang = param.name.name[this.defaultLanguages.market];
            if (marketLang && marketLang[this.defaultLanguages.lang]) {
              paramName = marketLang[this.defaultLanguages.lang];
            } else {
              paramName = param.name.key;
            }
          }
        });
      });
    });
    return paramName;
  }

  getParameter(element: OperationLite, commonKey: string): ParameterSettings {
    return element?.parameters?.[
      this.getParameterIndexByCommonKey(element.parameters as ParameterSettings[], commonKey)
    ] as ParameterSettings;
  }

  getGenericParameter(element: OperationLite, commonKey: { [p: string]: string }): ParameterSettings {
    return element?.parameters?.[
      this.getGenericParameterIndexByCommonKey(element.parameters as ParameterSettings[], commonKey)
    ] as ParameterSettings;
  }

  removeOperationFromSequence(sequenceIndex: number, operation: OperationLite): void {
    const sequence = this.formGroupStep?.controls.sequences.value[sequenceIndex];

    if (sequence && sequence.operations) {
      const operationIndex = sequence.operations.indexOf(operation);
      if (operationIndex > -1) {
        sequence.operations.splice(operationIndex, 1);
      }
    }

    this.createDataSource(this.formGroupStep);
    this.markAsDirty();
  }

  changeTime(field: ParameterSettings): void {
    const time = field.valueTemp?.replace(this.hourText, '').replace(this.minText, '').replace(this.secText, '');
    if (time) {
      field.value = Number(time.substr(0, 2)) * 3600 + Number(time.substr(2, 2)) * 60 + Number(time.substr(4, 2));
      this.checkErrorField(field);
    }
    this.markAsDirty();
  }

  markAsDirty(): void {
    this.formGroupStep?.controls.sequences.markAsDirty();
  }

  changeProgram(operation: OperationLite): void {
    this.filterParameters(operation);
    this.createDataSource(this.formGroupStep);
    this.markAsDirty();
  }

  updateProgramsParameters(operation: OperationLite, currentValue: ParameterSettings): ParameterSettings {
    const program = (this.programList ?? []).find((prog: Program) => prog.key === operation.program);

    if (program) {
      const updatedParams = (
        this.refDataService.toArray<ParameterSettings>((program.parameterSettings as unknown as DataRefsByKey<ParameterSettings>) ?? {}) ??
        []
      )
        .filter((param: ParameterSettings) => !this.knownPrograms.includes(param.name.key.toString()))
        .filter((param: ParameterSettings) => param.isVisible)
        .map((param: ParameterSettings) => {
          const paramCopy = { ...param };
          paramCopy.commonKey = param.name.key.toString();
          if (!this.knownPrograms.includes(paramCopy.commonKey)) {
            // Déterminer le type de champ en fonction des règles
            if (param.possibleValues && param.possibleValues.length > 0) {
              paramCopy.commonKey = 'DROPDOWN';
            } else if (param.bottomValue !== undefined && param.topValue !== undefined) {
              paramCopy.commonKey = 'NUMERIC';
              paramCopy.bottomValue = param.bottomValue;
              paramCopy.topValue = param.topValue;
            } else {
              paramCopy.commonKey = 'UNLOCKED';
            }
          }
          return paramCopy;
        }) as ProgramParameterLite[];

      const updatedParam = updatedParams.find((param) => param.key === currentValue.key);
      if (updatedParam) {
        return { ...currentValue, ...updatedParam };
      }
    }
    return currentValue;
  }

  filterParameters(operation: OperationLite): void {
    const program = (this.programList ?? []).find((prog: Program) => prog.key === operation.program);

    if (program) {
      operation.parameters = (
        this.refDataService.toArray<ParameterSettings>((program.parameterSettings as unknown as DataRefsByKey<ParameterSettings>) ?? {}) ??
        []
      )
        .filter((param: ParameterSettings) => param.isVisible)
        .map((param: ParameterSettings) => {
          const paramCopy = { ...param };
          paramCopy.commonKey = param.name.key.toString();
          paramCopy.value = param.defaultValue ?? '';
          if (!this.knownPrograms.includes(paramCopy.commonKey)) {
            // Déterminer le type de champ en fonction des règles
            if (param.possibleValues && param.possibleValues.length > 0) {
              paramCopy.commonKey = 'DROPDOWN';
              paramCopy.possibleValues = param.possibleValues;
              paramCopy.value = param.value;
            } else if (param.bottomValue !== undefined && param.topValue !== undefined) {
              paramCopy.commonKey = 'NUMERIC';
              paramCopy.bottomValue = param.bottomValue;
              paramCopy.topValue = param.topValue;
            } else {
              paramCopy.commonKey = 'UNLOCKED';
            }
          }
          return paramCopy;
        }) as ProgramParameterLite[];
    }
  }

  checkErrorField(field: ParameterSettings): void {
    if (typeof field.value !== 'number') {
      field.error = 'NaN';
    } else {
      if (field.bottomValue && field.value < field.bottomValue) {
        field.error = 'bottomValue';
      } else {
        if (field.topValue && field.value > field.topValue) {
          field.error = 'topValue';
        } else {
          delete field.error;
        }
      }
    }
    // check if someone have error and put the form in error
    let hasError = false;
    for (const sequence of this.formGroupStep?.controls?.sequences?.value) {
      if (sequence?.operations) {
        for (const operation of sequence.operations) {
          operation.parameters.forEach((param: ParameterSettings) => {
            if (param.error) {
              hasError = true;
            }
          });
        }
      }
    }
    if (hasError) {
      this.formGroupStep?.controls.sequences.setErrors({ programParameterError: true });
    } else {
      this.formGroupStep?.controls.sequences.setErrors(null);
    }
  }

  addEmptyOperationToSequence(index: number): void {
    //dont remove Partial type and don't add identifier field
    const newOperation: Partial<OperationLite> = {
      program: '',
      parameters: [],
    };

    if (this.formGroupStep && this.formGroupStep?.controls?.sequences?.value[index].operations === undefined) {
      this.formGroupStep.controls.sequences.value[index].operations = [];
    }

    this.formGroupStep?.controls?.sequences?.value[index].operations.push(newOperation);
    this.createDataSource(this.formGroupStep); // Re-create the data source to include new columns
    this.markAsDirty();
    this.changeDetectorRefs.detectChanges();
  }

  private subscribeToFormValueChanges(formGroup: UntypedFormGroup, field: string, handler: (value: any) => void): Subscription {
    const control = formGroup.controls[field];
    return control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(handler);
  }
}
